import { createElement } from 'react';
import { lucideIconByName } from '../../utils/lucideIcons';

interface ClaimLike {
  name: string;
  icon?: string | null;
  color?: string | null;
}

/** Fallback config used when icon/color are not set in the API response. */
const fallbackConfig: Record<string, { icon: string; color: string }> = {
  'Allergen Free': { icon: 'ShieldCheck', color: 'green' },
  'Highly Bioavailable': { icon: 'Beaker', color: 'cyan' },
  'Clean Label': { icon: 'Sparkles', color: 'emerald' },
  Dispersible: { icon: 'Wind', color: 'sky' },
  'Made in EU': { icon: 'Globe', color: 'blue' },
  'Low FODMAP': { icon: 'CircleCheck', color: 'lime' },
  'Gluten Free': { icon: 'WheatOff', color: 'orange' },
  Halal: { icon: 'Moon', color: 'emerald' },
  Kosher: { icon: 'Star', color: 'blue' },
  'Lactose Free': { icon: 'Milk', color: 'amber' },
  'Low Dosage': { icon: 'Pill', color: 'indigo' },
  Natural: { icon: 'Leaf', color: 'green' },
  'Non-GMO': { icon: 'DnaOff', color: 'blue' },
  Organic: { icon: 'Sprout', color: 'green' },
  Patented: { icon: 'Award', color: 'amber' },
  'Scientifically Proven': { icon: 'FlaskConical', color: 'purple' },
  Soluble: { icon: 'Droplets', color: 'sky' },
  Vegan: { icon: 'Vegan', color: 'green' },
  'WADA Approved': { icon: 'Dumbbell', color: 'red' },
};

/**
 * Tailwind v4 uses JIT and cannot resolve dynamically constructed class names
 * at build time. We keep a static map so every colour token appears as a full
 * literal string in the source code — guaranteeing it ends up in the generated
 * CSS even though the *value* is chosen at runtime.
 */
const colorClasses: Record<string, string> = {
  red: 'text-red-600 dark:text-red-400',
  orange: 'text-orange-600 dark:text-orange-400',
  amber: 'text-amber-600 dark:text-amber-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  lime: 'text-lime-600 dark:text-lime-400',
  green: 'text-green-600 dark:text-green-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  teal: 'text-teal-600 dark:text-teal-400',
  cyan: 'text-cyan-600 dark:text-cyan-400',
  sky: 'text-sky-600 dark:text-sky-400',
  blue: 'text-blue-600 dark:text-blue-400',
  indigo: 'text-indigo-600 dark:text-indigo-400',
  violet: 'text-violet-600 dark:text-violet-400',
  purple: 'text-purple-600 dark:text-purple-400',
  fuchsia: 'text-fuchsia-600 dark:text-fuchsia-400',
  pink: 'text-pink-600 dark:text-pink-400',
  rose: 'text-rose-600 dark:text-rose-400',
  slate: 'text-slate-600 dark:text-slate-400',
  gray: 'text-gray-600 dark:text-gray-400',
  zinc: 'text-zinc-600 dark:text-zinc-400',
  neutral: 'text-neutral-600 dark:text-neutral-400',
  stone: 'text-stone-600 dark:text-stone-400',
};

interface MarketingClaimBadgeProps {
  claim: ClaimLike;
  showLabel?: boolean;
}

export default function MarketingClaimBadge({
  claim,
  showLabel = false,
}: MarketingClaimBadgeProps) {
  const fallback = fallbackConfig[claim.name];
  const iconName = claim.icon ?? fallback?.icon;
  const colorName = claim.color ?? fallback?.color;

  const Icon = lucideIconByName(iconName);
  if (!Icon) return null;

  const colorClass = colorClasses[colorName ?? ''] ?? colorClasses.gray;

  if (showLabel) {
    return (
      <span
        title={claim.name}
        className={`inline-flex items-center gap-1 text-xs ${colorClass}`}
      >
        {createElement(Icon, { className: 'w-5 h-5 shrink-0' })}
        <span>{claim.name}</span>
      </span>
    );
  }

  return (
    <span className={`relative group inline-flex ${colorClass}`}>
      {createElement(Icon, { className: 'w-5 h-5' })}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2.5 py-1 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
        {claim.name}
      </span>
    </span>
  );
}
